import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiService } from '@/core/services/open-api/api.service';
import { HttpClient } from '@angular/common/http';
import {
    AvailableIntegrations,
    IntegrationConfig,
    IntegrationResource,
    IntegrationUnit,
} from '../../models/IntegrationEntity';
import {
    AutoImportRequestForm,
    AutoImportResponseForm,
    CheckUnitMappingRequestForm,
    CheckUnitMappingResponseForm,
    ImportResponse,
    InvoiceRequestForm,
    InvoiceResponseForm,
    ResourceImportRequestForm,
    ResourceTypes,
} from '@/core/models/integration-types';
import { catchError } from 'rxjs/operators';

/**
 * @class IntegrationRepository
 * @description Encapsulates raw API communication for integrations.
 * Serves as the data provider for IntegrationService, which acts as a
 * high-level abstraction layer to shield the application from low-level API logic.
 */
@Injectable({ providedIn: 'root' })
export class IntegrationRepository extends ApiService {
    constructor(http: HttpClient) {
        super(http);
    }

    public getAll(): Observable<IntegrationConfig[]> {
        return this._http.get<IntegrationConfig[]>(ApiService.privateApiUrl + 'integration').pipe(
            map((responseDto: IntegrationConfig[]) => {
                return responseDto; // TODO - implement proper model mapping like for activities (everywhere in this repo)
            }),
        );
    }

    public getAllWithAuthInfo(): Observable<IntegrationConfig[]> {
        return this._http.get<IntegrationConfig[]>(ApiService.privateApiUrl + 'integration?checkAuth=true').pipe(
            map((responseDto: IntegrationConfig[]) => {
                return responseDto;
            }),
        );
    }

    public getByService(service: string): Observable<IntegrationConfig> {
        return this._http.get<IntegrationConfig>(ApiService.privateApiUrl + 'integration/' + service).pipe(
            map((responseDto: IntegrationConfig) => {
                return responseDto;
            }),
        );
    }

    public deleteByService(service: string): Observable<void> {
        return this._http.delete<void>(ApiService.privateApiUrl + 'integration/' + service);
    }

    public create(intConfig: IntegrationConfig): Observable<IntegrationConfig> {
        const dto = intConfig; // this.mapNewActivityToDto(activity);
        return this._http.post<IntegrationConfig>(ApiService.privateApiUrl + 'integration/', dto).pipe(
            map((responseDto: IntegrationConfig) => {
                return responseDto;
            }),
        );
    }

    public update(intConfig: IntegrationConfig): Observable<IntegrationConfig> {
        const dto = intConfig; // this.mapActivityToDto(activity);
        return this._http.put<IntegrationConfig>(ApiService.privateApiUrl + 'integration/' + dto.service, dto).pipe(
            map((responseDto: IntegrationConfig) => {
                return responseDto;
            }),
        );
    }

    public patch(service: AvailableIntegrations, intConfig: IntegrationConfig): Observable<IntegrationConfig> {
        const dto = {
            ...intConfig,
            service: service,
        };
        return this._http.patch<IntegrationConfig>(ApiService.privateApiUrl + 'integration/' + service, dto).pipe(
            map((responseDto: IntegrationConfig) => {
                return responseDto;
            }),
        );
    }

    private getServiceUrl(service: string): string {
        return `${ApiService.privateApiUrl}integration/${service}`;
    }

    /**
     * Fetches resources of the accounting service, for example, contacts or service articles
     * @param service The service to fetch the contacts from, e.g. "sevdesk", "lexware"
     * @param resourceType The type of the resource, e.g. "contacts" or "services"
     * @returns Observable of a list of service resources
     */
    public getResources(service: string, resourceType: ResourceTypes): Observable<IntegrationResource[]> {
        const url = this.getServiceUrl(service) + '/resources/' + resourceType;
        return this._http.get<IntegrationResource[]>(url);
    }

    public getUnits(service: string): Observable<IntegrationUnit[]> {
        return this._http.get<IntegrationUnit[]>(this.getServiceUrl(service) + '/units');
    }

    /**
     * Checks if the unit mapping is configured for all tiles with external refs
     *
     * @param service The integration service identifier, e.g., sevdesk
     * @param tileIds The tiles to be checked
     * @returns An observable that emits a boolean indicating if the unit mapping is configured for all tiles with external refs.
     */
    public checkUnitMapping(
        service: string,
        requestBody: CheckUnitMappingRequestForm,
    ): Observable<CheckUnitMappingResponseForm> {
        const url = this.getServiceUrl(service) + '/check-unit-mapping';
        return this._http.post<CheckUnitMappingResponseForm>(url, requestBody);
    }

    /**
     * Creates an invoice in the specified service.
     * @param service The service to fetch the contacts from, e.g. "sevdesk", "lexware"
     * @param invoiceRequest The invoice request data.
     * @returns An Observable of the created invoice response.
     */
    public createInvoice(service: string, invoiceRequest: InvoiceRequestForm): Observable<InvoiceResponseForm> {
        const url = this.getServiceUrl(service) + '/invoice';
        return this._http.post<InvoiceResponseForm>(url, invoiceRequest).pipe(
            map((response) => {
                return response;
            }),
            /*
            tap(() => {
                this.resetAuthState();
            }),
            */
            catchError((error) => {
                console.error(`Error creating ${service} invoice:`, error);
                throw error;
            }),
        );
    }

    public importResources(service: string, importRequest: ResourceImportRequestForm): Observable<ImportResponse> {
        const url = this.getServiceUrl(service) + '/import-resources';
        return this._http.post<ImportResponse>(url, importRequest).pipe(
            map((response) => {
                return response;
            }),
            catchError((error) => {
                console.error(`Error importing resources for service "${service}":`, error);
                throw error;
            }),
        );
    }

    public getOAuth2InitUrl(service: string): Observable<{ url: string }> {
        return this._http.get<{ url: string }>(ApiService.privateApiUrl + 'integration/' + service + '/oauth2-init');
    }

    /**
     * Registers the integration.
     * @param service The service to register
     * @param params Generic key-value parameters sent in the request body (e.g. { iid: '...' })
     * @returns An observable of the integration configuration.
     */
    public register(service: string, params: Record<string, string>): Observable<IntegrationConfig> {
        return this._http.post<IntegrationConfig>(ApiService.privateApiUrl + `integration/${service}/register`, params);
    }

    public configureAutoImport(
        service: string,
        enable: boolean,
        layerId: number,
        resourceType: ResourceTypes,
    ): Observable<AutoImportResponseForm> {
        const url = this.getServiceUrl(service) + '/auto-import';
        const body: AutoImportRequestForm = { layerId, resourceType, autoImport: enable };
        return this._http.post<AutoImportResponseForm>(url, body).pipe(
            map((response) => {
                return response;
            }),
            catchError((error) => {
                console.error(`Error configuring auto-import for service "${service}":`, error);
                throw error;
            }),
        );
    }
}
